import React from "react";
import { useTheme } from "@mui/material";
import { LuLock } from "react-icons/lu";
import { METRIC_ICONS } from "./subscriptionUi";
import { formatLimit, formatNumber, METRICS } from "../../utils/subscriptionData";

const TONES = {
  ok: { bar: "#25408f", icon: "#25408f", border: null },
  full: { bar: "#b40909", icon: "#b40909", border: "#f1b3ad" },
  unlimited: { bar: "#c9d6f0", icon: "#25408f", border: null },
  locked: { bar: "#25408f", icon: "#25408f", border: null },
};
const TRACK = "#e4e9f2";

// One allowance as a stat card: the name on top, the used number large, what is left under it, and a thin bar along
// the bottom edge. The cards sit in one row that fills the width (five across on a wide screen), so there is no
// dead space. The plan / add-on split is a small line, only on the cards that have one. Blue while there is room,
// red once it is used up; an allowance with no cap gets a dashed line, since a bar would suggest a limit.
// The limit already includes add-ons.
// size: "sm" (small, five across), "md" (medium, for the organisation modal) or "lg" (the Usage page)
// locked: the plan has ended, so the allowance is frozen: greyed out, "Paused", an empty bar
// scaleMax: while locked there is no limit to measure against, so the bar is the share of the busiest allowance
const StatCard = ({ metric, label, usage, base, extra, oneTime, size, locked, scaleMax }) => {
  const theme = useTheme();
  const pick = (sm, md, lg) => (size === "sm" ? sm : size === "md" ? md : lg);
  const Icon = METRIC_ICONS[metric];
  const { used, limit } = usage;
  const unlimited = limit === null || limit === undefined;
  const full = !locked && !unlimited && used >= limit;
  const tone = TONES[locked ? "locked" : unlimited ? "unlimited" : full ? "full" : "ok"];
  const percent = locked ? Math.min(100, (used / Math.max(scaleMax || 0, 1)) * 100) : unlimited ? 100 : Math.min(100, (used / (limit || 1)) * 100);
  const hasSplit = !unlimited && extra > 0 && typeof base === "number";

  const status = locked ? "" : unlimited ? "Unlimited" : full ? "Limit reached" : `${formatNumber(limit - used)} left`;
  const notes = [hasSplit && `${formatNumber(base)} plan + ${formatNumber(extra)} add-on`, oneTime && "One-time"].filter(Boolean);

  return (
    <li className={`flex flex-col overflow-hidden rounded-xl border ${locked ? "bg-[#f8f7f7]" : "bg-white"} ${pick("p-2.5", "p-3", "min-h-[11rem] p-5")}`} style={{ borderColor: locked ? "#bcc2cc" : tone.border || theme.palette.secondary.v4 }}>
      <div className="contents">
        <p className={`flex items-start font-medium leading-tight text-[#2b3445] ${pick("gap-1.5 text-xs", "gap-2 text-sm", "gap-2 text-base")}`}>
          {Icon && <Icon className={`shrink-0 ${pick("mt-px h-3.5 w-3.5", "mt-px h-4 w-4", "mt-0.5 h-5 w-5")}`} style={{ color: tone.icon }} aria-hidden="true" />}
          <span>{label}</span>
        </p>

        <p className={`leading-none ${pick("mt-2", "mt-2", "mt-4")}`}>
          <span className={`font-bold tabular-nums ${pick("text-xl", "text-3xl", "text-5xl")}`}>{formatNumber(used)}</span>
          <span className={`text-[#4b5565] ${pick("text-sm", "text-base", "text-xl")}`}>{unlimited || locked ? " used" : ` / ${formatLimit(limit)}`}</span>
        </p>

        {locked ? null : size === "md" ? (
          <p className="mt-1 text-xs leading-tight text-[#4b5565]">
            <span className={full ? "font-semibold text-[#b40909]" : ""}>{status}</span>
            {notes.length > 0 && <span> · {notes.join(" · ")}</span>}
          </p>
        ) : (
          <>
            <p className={`leading-tight ${pick("mt-1 text-xs", "", "mt-2 text-base")} ${full ? "font-semibold text-[#b40909]" : "text-[#4b5565]"}`}>{status}</p>
            {notes.length > 0 && <p className={`leading-tight text-[#4b5565] ${pick("mt-0.5 text-[11px]", "", "mt-1 text-sm")}`}>{notes.join(" · ")}</p>}
          </>
        )}

        <div className={`mt-auto ${pick("pt-2.5", "pt-2", "pt-5")}`}>
          {/* A solid bar in every state: grey and measured against the busiest allowance when frozen, full and pale when there is no cap */}
          <div
            className={`w-full overflow-hidden rounded-full ${pick("h-1.5", "h-1.5", "h-2.5")}`}
            style={{ backgroundColor: locked ? "#d7dce4" : TRACK }}
            {...(locked || unlimited
              ? { "aria-hidden": true }
              : {
                  role: "progressbar",
                  "aria-label": label,
                  "aria-valuemin": 0,
                  "aria-valuemax": limit,
                  "aria-valuenow": Math.min(used, limit),
                  "aria-valuetext": `${used} of ${limit} used${hasSplit ? `, ${base} from the plan and ${extra} from add-ons` : ""}`,
                })}
          >
            <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, backgroundColor: tone.bar }} />
          </div>
        </div>
      </div>
      {/* The lock is a message at the foot of the card, not a cover over it: the numbers stay as they were */}
      {locked && (
        <div
          className={`flex items-center gap-2.5 bg-[#fdecef] justify-center ${pick("-mx-2.5 -mb-2.5 mt-2.5 px-2.5 py-1.5", "-mx-3 -mb-3 mt-3 px-3 py-2", "-mx-5 -mb-5 mt-4 px-5 py-3")}`}
        >
          <LuLock className={`shrink-0 text-[#c0182f] ${pick("h-3.5 w-3.5", "h-4 w-4", "h-5 w-5")}`} strokeWidth={2} aria-hidden="true" />
          <p className="leading-tight">
            <span className={`block font-semibold text-[#c0182f] ${pick("text-xs", "text-xs", "text-sm")}`}>Locked</span>
          </p>
        </div>
      )}
    </li>
  );
};

// Usage as a title line and a row of stat cards. `plan` supplies the plan / add-on split and which allowances are
// one-time (trial totals); `usage` is the API's usage map. Allowances the API doesn't report are left out.
// compact: slightly smaller cards, for the organisation modal where space is short
// fill: stretch to the height of the parent, so the cards share the space instead of leaving it empty
// locked: the plan has ended, so every allowance is shown frozen
const UsageBreakdown = ({ plan, usage, description = "", compact = false, fill = false, locked = false }) => {
  const rows = METRICS.filter(({ key }) => usage[key]);
  const size = compact ? "sm" : fill ? "md" : "lg";
  const scaleMax = Math.max(0, ...rows.map(({ key }) => usage[key].used || 0));
  return (
    <section aria-label="Usage" className={fill ? "flex h-full flex-col" : undefined}>
      <div className={`flex flex-wrap items-baseline gap-x-3 ${size === "lg" ? "mb-3" : "mb-2"}`}>
        <h3 className={`font-semibold ${size === "sm" ? "text-sm" : size === "md" ? "text-base" : "text-lg"}`}>Usage</h3>
        {description && <p className={`text-accent-gray-light ${size === "lg" ? "text-sm" : "text-xs"}`}>{description}</p>}
      </div>
      <ul className={`grid gap-2 ${fill ? "flex-1 auto-rows-fr" : ""} ${size === "sm" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" : size === "md" ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"}`}>
        {rows.map(({ key, label }) => (
          <StatCard key={key} metric={key} label={label} usage={usage[key]} base={plan?.base_limits?.[key]} extra={plan?.addons?.[key]} oneTime={plan?.limit_periods?.[key] === "total"} size={size} locked={locked} scaleMax={scaleMax} />
        ))}
      </ul>
    </section>
  );
};

export default UsageBreakdown;
